//src/hooks/useTenants.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTenant,
  createTenantOnboarding,
  downloadTenantOnboardingOrganizationImportTemplate,
  getTenantDetail,
  getTenantOnboardingStatus,
  getTenantOnboardingTemplates,
  getTenantSmtpSettings,
  listTenants,
  sendTenantOnboardingWelcomeEmail,
  setTenantActive,
  validateTenantOnboardingOrganizationImport,
  testTenantSmtpSettings,
  updateTenant,
  upsertTenantSmtpSettings,
  type ListTenantsParams,
  type TenantOnboardingInput,
  type TenantSmtpSettingsInput,
  type TenantSmtpSettingsTestInput,
  type TenantUpsertIn,
} from "@/services/tenants";

export const TENANTS_QK = (params: ListTenantsParams) =>
  ["tenants", params] as const;

export function useTenants(params: ListTenantsParams) {
  return useQuery({
    queryKey: TENANTS_QK(params),
    queryFn: () => listTenants(params),
    staleTime: 20_000,
  });
}

export function useTenantDetail(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["tenant_detail", tenantId],
    queryFn: () => getTenantDetail(tenantId as string),
    enabled: !!tenantId,
    staleTime: 20_000,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: TenantUpsertIn) => createTenant(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TenantUpsertIn }) =>
      updateTenant(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant_detail", variables.id] });
    },
  });
}

export function useSetTenantActive() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setTenantActive(id, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant_detail", variables.id] });
    },
  });
}


export function useTenantSmtpSettings(tenantId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["tenant_smtp_settings", tenantId],
    queryFn: () => getTenantSmtpSettings(tenantId as string),
    enabled: !!tenantId && enabled,
    staleTime: 10_000,
  });
}

export function useUpsertTenantSmtpSettings(tenantId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: TenantSmtpSettingsInput) => upsertTenantSmtpSettings(tenantId as string, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant_smtp_settings", tenantId] });
    },
  });
}

export function useTestTenantSmtpSettings(tenantId: string | undefined) {
  return useMutation({
    mutationFn: (body: TenantSmtpSettingsTestInput) => testTenantSmtpSettings(tenantId as string, body),
  });
}



export function useTenantOnboardingTemplates(tenantId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["tenant_onboarding_templates", tenantId],
    queryFn: () => getTenantOnboardingTemplates(tenantId as string),
    enabled: !!tenantId && enabled,
    staleTime: 20_000,
  });
}

export function useValidateTenantOnboardingOrganizationImport(tenantId: string | undefined) {
  return useMutation({
    mutationFn: (body: { filename: string; content_base64: string }) =>
      validateTenantOnboardingOrganizationImport(tenantId as string, body),
  });
}

export function useDownloadTenantOnboardingOrganizationImportTemplate() {
  return useMutation({
    mutationFn: () => downloadTenantOnboardingOrganizationImportTemplate(),
  });
}

export function useTenantOnboardingStatus(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["tenant_onboarding", tenantId],
    queryFn: () => getTenantOnboardingStatus(tenantId as string),
    enabled: !!tenantId,
    staleTime: 10_000,
  });
}

export function useCreateTenantOnboarding(tenantId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: TenantOnboardingInput) => createTenantOnboarding(tenantId as string, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant_onboarding", tenantId] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}

export function useSendTenantOnboardingWelcomeEmail(tenantId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => sendTenantOnboardingWelcomeEmail(tenantId as string, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant_onboarding", tenantId] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}
